import type { PropsWithChildren } from 'react';
import { I18nextProvider } from 'react-i18next';

import { ToastProvider } from '@/components/Toast';
import { TooltipLayer } from '@/components/TooltipLayer';
import { SvgConvertHost } from '@/features/uploads/svgConvert/SvgConvertHost';
import i18n from '@/i18n';

import { ReportIssueDialogHost } from './ReportIssueDialogHost';

import { AppErrorBoundary } from './AppErrorBoundary';

/**
 * 全域 Providers
 * 統一注入跨全站的 Context，避免散落在 main.tsx。
 *
 * 目前包含：
 * - `AppErrorBoundary`：全站錯誤攔截(R224;掛**最外層**,連 i18n / Toast provider
 *   自己爆掉也接得住 —— 在此之前任一 render 例外 = 整站白畫面)
 * - `I18nextProvider`：react-i18next 預設 instance
 * - `ToastProvider`：全域訊息提示佇列
 * - `TooltipLayer`:全站唯一的懸停提示框(R393;CIS「寫 `title` 即升級成 `.mtip`」)
 * - `SvgConvertHost`:SVG 轉檔確認視窗(R415;視窗本體用到才載入)
 *
 * **R48 D3 redesign 砍**:`ThemePreferenceProvider`(月亮 / 太陽 色系 + 標準 / 放大
 * 字級)— 對齊 D3 啟動包「無暗模式 / 桌面優先」拍板。theme.css `[data-theme='sun']`
 * + `[data-density='large']` 兩個 selector 區塊一併砍。
 *
 * 未來可擴充:
 * - React Query / SWR Provider（接後端資料快取）
 */
export function Providers({ children }: PropsWithChildren) {
  return (
    <AppErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <ToastProvider>
          {children}
          {/* R249 — 回報對話框只掛這一份;三個入口共用(帳號選單 / 錯誤 toast 的動作鈕 /
              未來新增的位置)。掛在 ToastProvider **內**是刻意的:對話框本身不用 toast,
              但它與 toast 的 action 同一棵 tree 才能被那顆鈕開起來。
              ⚠️ `AppErrorBoundary` 的回報入口**不走這裡** —— 白畫面時整棵 children 已被
              卸載,這一份根本不會 render;那條路徑由 boundary 自己 render 一份。 */}
          <ReportIssueDialogHost />
          {/* R415 — SVG 轉檔確認視窗只掛這一份;全站圖片上傳入口經 `prepareImageUpload` 排進佇列。 */}
          <SvgConvertHost />
          {/* R393 — 全站 title → `.mtip` 浮層(D3 tooltip.js)。只掛這一份。 */}
          <TooltipLayer />
        </ToastProvider>
      </I18nextProvider>
    </AppErrorBoundary>
  );
}
